import { faRedo, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import 'cropperjs/dist/cropper.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper, { ReactCropperElement } from 'react-cropper';
import { useTranslation } from 'react-i18next';
import GetCroppedCanvasOptions = Cropper.GetCroppedCanvasOptions;

interface Props {
    image: Blob;
    onCrop: (blob: Blob) => void;
    aspectRatio?: number;
    cropOptions?: GetCroppedCanvasOptions;
    /** JPEG quality of the produced blob. Without this the canvas falls back to lossless PNG. */
    quality?: number;
}

const ImageHandler = ({ image, aspectRatio, onCrop, cropOptions, quality = 0.85 }: Props) => {
    const { t } = useTranslation('form');
    const cropperRef = useRef<ReactCropperElement>(null);
    const [isReady, setIsReady] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const src = useMemo(() => URL.createObjectURL(image), [image]);
    useEffect(() => () => URL.revokeObjectURL(src), [src]);

    const handleCrop = useCallback(() => {
        setErrorMessage(undefined);

        // Returns null while the cropper has not finished loading the image.
        const canvas = cropperRef.current?.cropper?.getCroppedCanvas(cropOptions);
        if (!canvas) {
            setErrorMessage(t('dropzone.cropFailed'));
            return;
        }

        canvas.toBlob(
            (blob) => (blob ? onCrop(blob) : setErrorMessage(t('dropzone.cropFailed'))),
            'image/jpeg',
            quality
        );
    }, [onCrop, cropOptions, quality, t]);

    const handleRotateLeft = () => cropperRef.current?.cropper.rotate(-45);
    const handleRotateRight = () => cropperRef.current?.cropper.rotate(45);

    return (
        <div className="tw-relative tw-min-h-[50vh]">
            <div className="tw-mb-4 tw-overflow-hidden tw-rounded-lg">
                <Cropper
                    ref={cropperRef}
                    src={src}
                    style={{ height: '50vh', width: '100%' }}
                    // Cropper.js options
                    viewMode={1}
                    dragMode="move"
                    cropBoxMovable={false}
                    cropBoxResizable={false}
                    aspectRatio={aspectRatio}
                    scalable={true}
                    movable={true}
                    guides={true}
                    background={false}
                    toggleDragModeOnDblclick={false}
                    ready={() => setIsReady(true)}
                />
            </div>
            <div className="tw-mb-2 tw-flex tw-gap-4">
                <Button color="secondary" size="xs" block disabled={!isReady} onPress={handleRotateLeft}>
                    <FontAwesomeIcon icon={faUndo} />
                </Button>
                <Button color="secondary" size="xs" block disabled={!isReady} onPress={handleRotateRight}>
                    <FontAwesomeIcon icon={faRedo} />
                </Button>
            </div>
            {errorMessage && <span className="tw-mb-2 tw-block tw-text-xs tw-text-red-500">{errorMessage}</span>}
            <Button block disabled={!isReady} onPress={handleCrop}>
                {isReady ? t('dropzone.crop') : t('dropzone.cropLoading')}
            </Button>
        </div>
    );
};

export default ImageHandler;
